import toast from "react-hot-toast";

import type { FileWithPath } from "../hooks/useS3Upload";

/**
 * DataTransfer（OS からの D&D）からフォルダ構造ごとファイルを取り出すヘルパー。
 * DropZoneOverlay / FileBrowser など複数のドロップ実装から共用する。
 */

/** FileSystemFileEntryからFileを取得 */
const getFileFromEntry = (entry: FileSystemFileEntry): Promise<File> => {
  return new Promise((resolve, reject) => {
    entry.file(resolve, reject);
  });
};

/** FileSystemDirectoryEntryから全エントリを取得 */
const readDirectoryEntries = (
  reader: FileSystemDirectoryReader,
): Promise<FileSystemEntry[]> => {
  return new Promise((resolve, reject) => {
    reader.readEntries(resolve, reject);
  });
};

/**
 * 取り出せなかったエントリをユーザーへ通知する。
 *
 * `entry.file()` は OneDrive / Google ドライブの「オンラインのみ」ファイル、
 * ネットワークドライブ上のファイル、ドラッグ中に移動・削除されたファイルで失敗する。
 * ここで黙って握りつぶすとドロップしたのに 1 件だけ消えたように見えるため、
 * 呼び出し元が通知を忘れられないようユーティリティ側で必ず警告する。
 */
const notifyUnreadable = (paths: string[]): void => {
  if (paths.length === 0) return;

  const head = paths.slice(0, 3).join("、");
  const rest = paths.length > 3 ? ` ほか${paths.length - 3}件` : "";
  toast.error(
    `${paths.length}件のファイルを読み取れませんでした（${head}${rest}）。` +
      "クラウドストレージのオンライン専用ファイルやネットワークドライブ上のファイルは、" +
      "端末にダウンロードしてからもう一度ドロップしてください。",
    { duration: 10000 },
  );
};

/** ディレクトリを再帰的に読み取り、FileWithPath配列を返す */
const readDirectoryRecursively = async (
  dirEntry: FileSystemDirectoryEntry,
  basePath: string = "",
  unreadable: string[],
): Promise<FileWithPath[]> => {
  const results: FileWithPath[] = [];
  const reader = dirEntry.createReader();

  // readEntriesは一度に全エントリを返さない場合があるので、空になるまで繰り返す
  const entries: FileSystemEntry[] = [];
  let batch: FileSystemEntry[];
  do {
    try {
      batch = await readDirectoryEntries(reader);
    } catch (error) {
      // 途中で読めなくなっても、それまでに拾えたファイルは活かして警告に回す
      console.warn(`Failed to read directory: ${basePath}`, error);
      unreadable.push(
        `${basePath || dirEntry.name}/（フォルダの読み取り失敗）`,
      );
      break;
    }
    entries.push(...batch);
  } while (batch.length > 0);

  for (const entry of entries) {
    const currentPath = basePath ? `${basePath}/${entry.name}` : entry.name;

    if (entry.isFile) {
      try {
        const file = await getFileFromEntry(entry as FileSystemFileEntry);
        results.push({ file, relativePath: currentPath });
      } catch (error) {
        console.warn(`Failed to read file: ${currentPath}`, error);
        unreadable.push(currentPath);
      }
    } else if (entry.isDirectory) {
      const subResults = await readDirectoryRecursively(
        entry as FileSystemDirectoryEntry,
        currentPath,
        unreadable,
      );
      results.push(...subResults);
    }
  }

  return results;
};

/**
 * OS からのファイル / フォルダドラッグか（アプリ内 D&D と区別する）。
 * dataTransfer が null になりうるネイティブの DragEvent もそのまま渡せる。
 */
export const containsNativeFiles = (e: {
  dataTransfer: DataTransfer | null;
}): boolean =>
  !!e.dataTransfer && Array.from(e.dataTransfer.types).includes("Files");

/**
 * drop イベントから FileWithPath 配列を取り出す。
 * DataTransferItem の webkitGetAsEntry でフォルダを再帰走査し、失敗時や
 * 非対応ブラウザでは dataTransfer.files（フラット）にフォールバックする。
 * dataTransfer はイベント終了後に無効になるため、同期的に呼び出すこと。
 */
export const extractDropEntries = (e: {
  dataTransfer: DataTransfer;
}): Promise<FileWithPath[]> => {
  const fallback: FileWithPath[] = Array.from(e.dataTransfer.files).map(
    (file) => ({ file, relativePath: file.name }),
  );
  if (!e.dataTransfer.items || e.dataTransfer.items.length === 0) {
    return Promise.resolve(fallback);
  }
  return getFilesFromDataTransferItems(e.dataTransfer.items).then(
    (entries) => (entries.length > 0 ? entries : fallback),
    () => fallback,
  );
};

/**
 * DataTransferItemListからFileWithPath配列を取得（フォルダ対応）。
 * 注意: Phase 1 は DataTransfer が生きているうちに同期実行されるため、
 * drop イベントハンドラ内から直接呼び出すこと（await を挟んでからの呼び出し不可）。
 */
export const getFilesFromDataTransferItems = async (
  items: DataTransferItemList,
): Promise<FileWithPath[]> => {
  const results: FileWithPath[] = [];
  // 読み取れなかったエントリ。走査の最後にまとめて通知する
  const unreadable: string[] = [];

  // Phase 1: DataTransferが有効なうちに全エントリを同期的に収集
  // (awaitを挟むとブラウザがDataTransferをクリアし、後続アイテムが取得不能になる)
  const entries: { entry: FileSystemEntry | null; file: File | null }[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== "file") continue;

    const entry = item.webkitGetAsEntry?.() || null;
    const file = entry ? null : item.getAsFile();
    entries.push({ entry, file });
  }

  // Phase 2: 収集したエントリを非同期で処理
  for (const { entry, file } of entries) {
    if (!entry) {
      if (file) {
        results.push({ file, relativePath: file.name });
      } else {
        // entry も File も取れないアイテム（ドラッグ元が実体を渡せなかった等）
        unreadable.push("（ファイル名を取得できないファイル）");
      }
      continue;
    }

    if (entry.isFile) {
      try {
        const resolvedFile = await getFileFromEntry(
          entry as FileSystemFileEntry,
        );
        results.push({ file: resolvedFile, relativePath: entry.name });
      } catch (error) {
        console.warn(`Failed to read file: ${entry.name}`, error);
        unreadable.push(entry.name);
      }
    } else if (entry.isDirectory) {
      const subResults = await readDirectoryRecursively(
        entry as FileSystemDirectoryEntry,
        entry.name,
        unreadable,
      );
      results.push(...subResults);
    }
  }

  notifyUnreadable(unreadable);

  return results;
};
